import { formatScore, type SetupOrigin } from '@mogzo/shared';
import { type FormEvent, useState } from 'react';
import { ApiError, apiSend, type Project } from '../lib/api';
import { timeAgo } from '../lib/format';
import { useApi } from '../lib/use-api';
import { BoardForm } from './BoardForm';
import { CliTag, MadeFrom, touchedByCli } from './MadeFrom';
import { Alert, Button, cardClass, ErrorNotice, fieldClass, Loading } from './ui';

type Env = 'test' | 'prod';

interface Board {
  id: string;
  name: string;
  sort: 'asc' | 'desc';
  format: 'number' | 'time';
  reset: 'none' | 'daily' | 'weekly' | 'monthly';
  min: number | null;
  max: number | null;
  defined: boolean;
  hasScores: boolean;
  period: string;
  players: number;
  periods: string[];
  /** Where it was made and last changed; null for a board that's no longer made. */
  origin: SetupOrigin | null;
}
interface Entry {
  entryId: number;
  rank: number;
  player: { playerId: string; name: string | null };
  score: number;
  submittedAt: string;
}
interface BoardPage {
  period: string;
  total: number;
  refused: { out_of_range: number; too_fast: number };
  entries: Entry[];
}

const REASONS = [
  ['cheating', 'Looks like cheating'],
  ['bug', 'A bug in my game'],
  ['other', 'Other'],
] as const;

const CURRENT: Record<Board['reset'], string> = {
  none: 'All time',
  daily: 'Today · resets 00:00 UTC',
  weekly: 'This week · resets Mon 00:00 UTC',
  monthly: 'This month · resets on the 1st, 00:00 UTC',
};

function describe(board: Board): string {
  const parts = [board.sort === 'desc' ? 'Highest first' : 'Lowest first'];
  if (board.format === 'time') parts.push('shown as time');
  parts.push(board.reset === 'none' ? 'all time' : `resets ${board.reset}`);
  parts.push(`${board.players} ${board.players === 1 ? 'player' : 'players'}`);
  return parts.join(' · ');
}

function bounds(board: Board): string {
  const show = (value: number) => formatScore(value, board.format);
  if (board.min !== null && board.max !== null)
    return `Scores from ${show(board.min)} to ${show(board.max)}`;
  if (board.min !== null) return `Scores of ${show(board.min)} or more`;
  if (board.max !== null) return `Scores up to ${show(board.max)}`;
  return 'Any score';
}

function failure(error: unknown): string {
  return error instanceof ApiError ? error.message : 'Could not reach the API. Try again.';
}

/** A score's row, with Remove asking why before it goes (a cheat, a bug). */
function EntryRow({
  entry,
  board,
  onRemove,
}: {
  entry: Entry;
  board: Board;
  onRemove: (reason: string) => Promise<void>;
}) {
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState<string>('cheating');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const who = entry.player.name ?? `Player ${entry.player.playerId}`;
  const score = formatScore(entry.score, board.format);

  async function remove(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onRemove(reason);
    } catch (failed) {
      setError(failure(failed));
      setBusy(false);
    }
  }

  return (
    <>
      <tr className={confirming ? 'bg-danger-soft' : ''}>
        <td className="border-b border-divider px-3 py-3 font-semibold">{entry.rank}</td>
        <td className="border-b border-divider px-3 py-3">
          {entry.player.name ? (
            <>
              <strong>{entry.player.name}</strong>
              <br />
              <span className="font-mono text-[13px] text-muted">{entry.player.playerId}</span>
            </>
          ) : (
            <span className="font-mono text-[13px]">{who}</span>
          )}
        </td>
        <td className="border-b border-divider px-3 py-3 text-right font-semibold">{score}</td>
        <td className="border-b border-divider px-3 py-3 text-muted">
          {timeAgo(entry.submittedAt)}
        </td>
        <td className="border-b border-divider px-3 py-3 text-right">
          {!confirming && (
            <Button
              variant="danger"
              className="min-h-10 px-3.5"
              aria-label={`Remove ${who}’s score`}
              onClick={() => setConfirming(true)}
            >
              Remove
            </Button>
          )}
        </td>
      </tr>
      {confirming && (
        <tr>
          <td colSpan={5} className="border-b border-divider bg-danger-soft p-4">
            <form onSubmit={remove} className="flex flex-col gap-3">
              <strong className="text-danger">
                Remove {who}’s score of {score}?
              </strong>
              <p className="text-muted">
                It leaves this board. The player can still play and send new scores. This is kept in
                your project’s history.
              </p>
              {error && <Alert tone="error">{error}</Alert>}
              <label className="flex max-w-80 flex-col gap-1.5 text-[13px] font-semibold text-muted">
                Why (for your records)
                <select
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  className={fieldClass}
                >
                  {REASONS.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex flex-wrap gap-2.5">
                <button type="submit" disabled={busy} className="mz-btn mz-btn-danger">
                  {busy ? 'Removing…' : 'Yes, remove'}
                </button>
                <Button variant="secondary" onClick={() => setConfirming(false)} disabled={busy}>
                  Keep it
                </Button>
              </div>
            </form>
          </td>
        </tr>
      )}
    </>
  );
}

/** The Leaderboards tab of the data browser (M17), from the approved mockup. */
export function Leaderboards({
  project,
  env,
  refresh,
}: {
  project: Project;
  env: Env;
  refresh: number;
}) {
  const [reload, setReload] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [period, setPeriod] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  /** The form in place of the board: a new board, or the board being edited. */
  const [editing, setEditing] = useState<'new' | Board | null>(null);
  const base = `/v1/dev/projects/${project.id}/leaderboards`;
  const boards = useApi<{ boards: Board[] }>(`${base}?env=${env}`, reload + refresh);
  const list = boards.status === 'ready' ? boards.data.boards : [];
  const board = list.find((b) => b.id === selected) ?? list[0] ?? null;
  const shownPeriod = board && period && board.periods.includes(period) ? period : board?.period;

  const params = new URLSearchParams({ env, offset: String(offset) });
  if (shownPeriod) params.set('period', shownPeriod);
  if (query) params.set('search', query);
  const page = useApi<BoardPage>(board ? `${base}/${board.id}?${params}` : null, reload + refresh);

  if (boards.status === 'loading') return <Loading />;
  if (boards.status === 'error') return <ErrorNotice error={boards.error} />;
  if (editing) {
    return (
      <BoardForm
        project={project}
        board={editing === 'new' ? undefined : editing}
        onCancel={() => setEditing(null)}
        onDone={(id) => {
          setEditing(null);
          setSelected(id);
          setPeriod(null);
          setReload((n) => n + 1);
        }}
      />
    );
  }
  if (!board) {
    return (
      <section className={`${cardClass} flex flex-col items-start gap-3 p-6`}>
        <h2 className="text-[15px] font-semibold">No leaderboards yet</h2>
        {env === 'test' ? (
          <>
            <p className="text-muted">
              Make one here, then send scores to it from your game with{' '}
              <span className="font-mono">mogzo.leaderboards.submit</span>.
            </p>
            <Button variant="brand" onClick={() => setEditing('new')}>
              New leaderboard
            </Button>
          </>
        ) : (
          <p className="text-muted">
            Live boards come from your last published version, once you publish one.
          </p>
        )}
      </section>
    );
  }

  return (
    <div className="flex flex-wrap items-start gap-5">
      <section
        aria-labelledby="boards-heading"
        className={`${cardClass} flex max-w-[340px] flex-[1_1_260px] flex-col gap-3 p-4`}
      >
        <div className="flex items-center justify-between gap-2">
          <h2 id="boards-heading" className="text-[15px] font-semibold">
            Boards
          </h2>
          {env === 'test' && (
            <Button variant="brand" className="min-h-10 px-4" onClick={() => setEditing('new')}>
              New leaderboard
            </Button>
          )}
        </div>
        <p className="text-sm text-muted">
          {env === 'test'
            ? 'Made here. Your test community gets changes right away; live players get them when you publish a version.'
            : 'From your last published version. New and changed boards reach live players when you publish.'}
        </p>
        <ul className="flex flex-col gap-1">
          {list.map((b) => (
            <li key={b.id}>
              <button
                type="button"
                aria-pressed={b.id === board.id}
                onClick={() => {
                  setSelected(b.id);
                  setPeriod(null);
                  setOffset(0);
                }}
                className={`flex w-full flex-col gap-0.5 rounded-[6px] border px-3 py-2.5 text-left ${
                  b.id === board.id
                    ? 'border-brand bg-cream'
                    : 'border-transparent hover:border-line'
                }`}
              >
                <strong>
                  {b.name}
                  {env === 'test' && touchedByCli(b.origin) && <CliTag />}
                </strong>
                <span className="font-mono text-[13px] text-muted">{b.id}</span>
                <span className="text-[13px] text-muted">
                  {b.defined ? describe(b) : 'Deleted, or not published to live yet'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </section>

      <div className="flex min-w-0 flex-[999_1_440px] flex-col gap-5">
        <section aria-labelledby="board-heading" className={`${cardClass} flex flex-col gap-3 p-4`}>
          <div className="flex flex-wrap items-end justify-between gap-3 px-1">
            <div>
              <h2 id="board-heading" className="text-[15px] font-semibold">
                {board.name}
              </h2>
              <p className="mt-1 text-[13px] text-muted">
                {bounds(board)} · each player’s best{' '}
                {board.reset === 'none' ? 'counts' : 'this period counts'}
              </p>
              {env === 'test' && <MadeFrom origin={board.origin} />}
              {env === 'test' && board.defined && (
                <Button
                  variant="secondary"
                  className="mt-2 min-h-10 px-4"
                  onClick={() => setEditing(board)}
                >
                  Edit board
                </Button>
              )}
            </div>
            {board.reset !== 'none' && (
              <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-muted">
                Period
                <select
                  value={shownPeriod}
                  onChange={(event) => {
                    setPeriod(event.target.value);
                    setOffset(0);
                  }}
                  className={fieldClass}
                >
                  {board.periods.map((p) => (
                    <option key={p} value={p}>
                      {p === board.period ? CURRENT[board.reset] : `${p} (finished)`}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          <form
            className="px-1"
            onSubmit={(event) => {
              event.preventDefault();
              setQuery(search.trim());
              setOffset(0);
            }}
          >
            <label className="flex max-w-96 flex-col gap-1.5 text-[13px] font-semibold text-muted">
              {env === 'test' ? 'Find a player' : 'Find a player id'}
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={env === 'test' ? 'Name or player id' : 'Player id'}
                className={fieldClass}
              />
            </label>
          </form>
          {page.status === 'loading' && <Loading />}
          {page.status === 'error' && <ErrorNotice error={page.error} />}
          {page.status === 'ready' &&
            (page.data.entries.length === 0 ? (
              <p className="px-1 py-4 text-sm text-muted">
                {query ? 'No player matches.' : 'No scores in this period yet.'}
              </p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="text-left text-xs text-muted">
                        <th className="border-b border-divider px-3 py-2.5">Rank</th>
                        <th className="border-b border-divider px-3 py-2.5">Player</th>
                        <th className="border-b border-divider px-3 py-2.5 text-right">Score</th>
                        <th className="border-b border-divider px-3 py-2.5">Submitted</th>
                        <th className="border-b border-divider px-3 py-2.5">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {page.data.entries.map((entry) => (
                        <EntryRow
                          key={entry.entryId}
                          entry={entry}
                          board={board}
                          onRemove={async (reason) => {
                            await apiSend('DELETE', `${base}/entries/${entry.entryId}?env=${env}`, {
                              reason,
                            });
                            setReload((n) => n + 1);
                          }}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="px-1 pb-2 text-sm text-muted">
                  Showing {offset + 1}–{offset + page.data.entries.length} of {page.data.total}
                  {offset + page.data.entries.length < page.data.total && (
                    <>
                      {' · '}
                      <button
                        type="button"
                        className="font-semibold underline decoration-brand decoration-2 underline-offset-4"
                        onClick={() => setOffset(offset + 50)}
                      >
                        Show more
                      </button>
                    </>
                  )}
                </p>
              </>
            ))}
        </section>

        {page.status === 'ready' && (
          <section
            aria-labelledby="refused-heading"
            className={`${cardClass} flex flex-col gap-2.5 p-5`}
          >
            <h2 id="refused-heading" className="text-[15px] font-semibold">
              Refused {shownPeriod === board.period ? 'this period' : `in ${shownPeriod}`}
            </h2>
            <p className="text-muted">
              Scores MOGZO turned away, so you can spot a bug or a cheat.
            </p>
            <ul className="flex list-disc flex-col gap-1.5 pl-5 text-muted">
              <li>
                {page.data.refused.out_of_range}{' '}
                {page.data.refused.out_of_range === 1 ? 'score' : 'scores'} outside the board’s
                bounds
              </li>
              <li>
                {page.data.refused.too_fast} {page.data.refused.too_fast === 1 ? 'score' : 'scores'}{' '}
                sent too fast (over the per-minute limit)
              </li>
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}
